// i18n: translated
/**
 * トースト通知表示コンテナ
 *
 * 画面右上に通知を積み上げ表示する。
 * 各トーストに閉じるボタン付き。
 * `targetPath` 付きトースト (新着通知ポップアップの案件遷移、QA-093 系) は
 * 本文クリックで該当パスへ遷移する。
 *
 * 幅と描画形 (notification-banner-readability-20260820):
 * - コンテナは desktop 約 560px / mobile は viewport 内 (左右 12px) で頭打ちにする。
 *   上限が無いと長文が 1 行で流れて画面幅いっぱいを占有していた。
 * - `title` を持つトーストだけ「見出し + 2 行プレビュー + 行動導線」の構造化描画にする。
 *   持たない従来トーストは `message` 1 行のままで、表示も遷移も変わらない。
 * - プレビューは `line-clamp-2` で**見せる行数だけ**抑える (本文文字列は切り詰めない)。
 *
 * useNavigate を使うため Router context 内 (現状 AppLayout 配下のみ) でマウントすること。
 *
 * variant ごとのスタイルは `./toastStyles` の `TOAST_STYLES` を参照する
 * (旧 dark モード `text-gray-200` 共通指定の視認性バグ修正、2026-05-26)。
 */
import { useNavigate } from 'react-router-dom';
import { translateKnownMessage, useLocale, useT } from '@/i18n';
import { commonNs } from '@/i18n/namespaces/common';
import { useToast } from '@/hooks/useToast';
import type { ToastType } from '@/types';
import { TOAST_STYLES } from './toastStyles';

/** トースト種別ごとの SVG アイコン */
function ToastIcon({ type }: { type: ToastType }) {
  const cls = `h-5 w-5 ${TOAST_STYLES[type].icon}`;

  switch (type) {
    case 'success':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className={cls}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>
      );
    case 'error':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className={cls}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
          />
        </svg>
      );
    case 'warning':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className={cls}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
          />
        </svg>
      );
    case 'info':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className={cls}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
          />
        </svg>
      );
  }
}

export default function ToastContainer() {
  const { toasts, removeToast } = useToast();
  const navigate = useNavigate();
  // 表示言語 (admin-english-toggle-20260924)。権限不足・セッション切れ等の共通メッセージだけを
  // 表示直前に訳す (作る側の文字列は変えない。画面ごとの文言・サーバーの文言はそのまま)。
  const { locale } = useLocale();
  const t = useT(commonNs);

  if (toasts.length === 0) return null;

  return (
    // 幅の上限を持たせる (notification-banner-readability-20260820)。
    // fixed + right 指定のみ + width:auto は viewport に対する shrink-to-fit になるため、
    // 上限が無いと 1 行の長文が max-content 幅まで伸びて画面幅いっぱいを占有していた。
    // desktop は約 560px で頭打ちにし、mobile は左右 12px ずつの余白を残して viewport 内に収める
    // (max-w を viewport 基準にすることで横スクロールを発生させない)。
    <div className="fixed right-3 top-3 z-[60] flex max-w-[calc(100vw-1.5rem)] flex-col gap-2 sm:right-4 sm:top-4 sm:max-w-[560px]">
      {toasts.map((toast) => {
        const style = TOAST_STYLES[toast.type];
        // targetPath 付きトーストは本文クリックで遷移 (閉じるボタンは従来どおり独立)
        const targetPath = toast.targetPath;
        // title を持つトーストだけ「見出し + プレビュー + 行動導線」の構造化描画にする。
        // 持たない従来トースト (保存成功 / エラー / 集約通知 等) は message 1 行のまま。
        const structured = Boolean(toast.title);
        const content = structured ? (
          <>
            <ToastIcon type={toast.type} />
            {/* min-w-0 が無いと flex item が min-content 幅を下回れず、長い preview で
                カードが上限幅を突き破る */}
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-semibold ${style.text}`}>{toast.title}</p>
              {toast.preview && (
                // 本文文字列は切り詰めず、見せる行数だけを 2 行に抑える
                // (機械的な truncate は語の途中で切れて意味が変わるため)。
                <p className={`mt-0.5 line-clamp-2 break-words text-xs ${style.text}`}>
                  {toast.preview}
                </p>
              )}
              {toast.actionLabel && (
                <p className={`mt-1.5 text-xs font-medium ${style.text}`}>{toast.actionLabel} →</p>
              )}
            </div>
          </>
        ) : (
          <>
            <ToastIcon type={toast.type} />
            {/* 従来トーストの体裁は維持しつつ、上限幅を超えて溢れないようにだけ手当てする */}
            <p className={`min-w-0 flex-1 break-words text-sm font-medium ${style.text}`}>
              {translateKnownMessage(locale, toast.message)}
            </p>
          </>
        );
        return (
          <div
            key={toast.id}
            className={`flex items-start gap-3 rounded-lg border ${style.container} px-4 py-3 shadow-lg backdrop-blur-sm animate-in slide-in-from-right-5`}
            role="alert"
          >
            {targetPath ? (
              <button
                type="button"
                onClick={() => {
                  removeToast(toast.id);
                  navigate(targetPath);
                }}
                className="flex min-w-0 flex-1 cursor-pointer items-start gap-3 text-left"
                title={
                  toast.actionLabel
                    ? t('toast.clickToAction', { action: toast.actionLabel })
                    : t('toast.clickToOpenCase')
                }
              >
                {content}
              </button>
            ) : (
              content
            )}
            <button
              type="button"
              onClick={() => removeToast(toast.id)}
              className={`ml-2 rounded p-0.5 transition-colors ${style.close}`}
              aria-label={t('actions.close')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-4 w-4"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        );
      })}
    </div>
  );
}
